import { Box, Button, Card, CardContent, Divider, Grid, IconButton, Stack, Typography } from "@mui/material";
import { FormProvider, RHFDateField, RHFFileField, RHFRadioGroup, RHFSearchableSelectField, RHFTextField } from "../../common/hook-form";
import _ from "lodash";
import React, { useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { AddCircleTwoTone, RemoveCircleTwoTone } from "@mui/icons-material";
import UploadIcon from "@mui/icons-material/Upload";
import { LoadingButton } from "@mui/lab";
import { useGetMerchantQuery } from "../../redux/commonApiCalls";
import { addressString, filterByProp, formatDate } from "../../helpers/UtilityHelper";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import PageTitle from "../../components/PageTitle";
import { apiConstants, post, postGeneric } from "../../common/constants";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import Loader from "../../common/Loader";
import DownloadSampleFile from "../../components/DownloadSampleFile";
import PillFilters from "../AppointmentDeliveriesV2/shared/PillFilters";
import { FieldLabel } from "../../common/FilterBar";

// ─── Shared styled helpers ────────────────────────────────────────────────────

function FormCard({ children }) {
    return (
        <Card elevation={0} sx={{ borderRadius: '10px', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
                {children}
            </CardContent>
        </Card>
    );
}

const emptyItemObject = {
    units: 0,
    additional: { weight: 0 }
};

const PILLS = [
    { key: 0, label: 'Single Pickup' },
    { key: 1, label: 'Bulk Upload' },
];

// ─── Single form ──────────────────────────────────────────────────────────────

function SingleQuickPickupForm() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [isCreatingOrder, setIsCreatingOrder] = useState(false);

    const validationSchema = Yup.object().shape({
        merchant: Yup.string().required('Merchant is required'),
        warehouseName: Yup.string().required('Warehouse name is required'),
        pickup_date: Yup.date()
            .required('Pickup date is required')
            .nullable()
            .test('pickup-date-validation', 'Pickup date cannot be in the past. After 1 PM IST, only future dates are allowed.', function (value) {
                if (!value) return true;
                const now = new Date();
                const istOffset = 5.5 * 60 * 60 * 1000;
                const istTime = new Date(now.getTime() + istOffset);
                const currentHour = istTime.getUTCHours();
                const selectedDate = new Date(value);
                selectedDate.setHours(0, 0, 0, 0);
                let minAllowedDate;
                if (currentHour < 13) {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    minAllowedDate = today;
                } else {
                    const tomorrow = new Date();
                    tomorrow.setDate(tomorrow.getDate() + 1);
                    tomorrow.setHours(0, 0, 0, 0);
                    minAllowedDate = tomorrow;
                }
                return selectedDate >= minAllowedDate;
            }),
        payment_mode: Yup.string().required('Payment mode is required').oneOf(['PREPAID', 'COD'], 'Invalid payment mode'),
        zip: Yup.string().required('Delivery Zip Code is required').matches(/^\d{6}$/, 'Zip code must be exactly 6 digits'),
        item_list: Yup.array().of(
            Yup.object().shape({
                units: Yup.number().required('Number of units is required').positive('Must be a positive number').integer('Must be a whole number'),
                additional: Yup.object().shape({
                    weight: Yup.number().required('Weight is required').positive('Must be a positive number')
                })
            })
        ).required('At least one item is required')
    });

    const methods = useForm({
        defaultValues: {
            merchant: '',
            payment_mode: 'PREPAID',
            appointment_delivery: false,
            warehouseName: '',
            zip: '',
            pickup_date: null,
            item_list: [{ units: '', additional: { weight: '', height: 10, breadth: 10, length: 10 } }]
        },
        resolver: yupResolver(validationSchema)
    });

    const { handleSubmit, control, watch, setValue, reset } = methods;
    const invoiceValue = watch('invoice_value');
    const { append, remove, fields } = useFieldArray({ name: 'item_list', control });

    React.useEffect(() => {
        setValue('selling_price', invoiceValue);
        // eslint-disable-next-line
    }, [invoiceValue]);

    React.useEffect(() => {
        if (fields.length === 0) append(emptyItemObject);
        // eslint-disable-next-line
    }, [fields]);

    const merchantId = watch('merchant');
    const { data, isLoading, isFetching } = useGetMerchantQuery({ id: merchantId });
    const pickupLocations = filterByProp(data?.pickup_details, "is_deleted") || [];
    const pickupLocationOptions = _(pickupLocations)
        .filter({ status: 'ACTIVE', address_type: 'PICKUP' })
        .map((loc) => ({ value: loc.name, label: addressString(loc) }))
        .value();
    const isMerchantSelected = Boolean(merchantId);

    const onSubmit = (values) => {
        setIsCreatingOrder(true);
        const formData = JSON.parse(JSON.stringify(values));
        const request = {
            warehouseName: formData.warehouseName,
            merchantOid: merchantId,
            dropoffLocation: { zip: formData.zip },
            dimensions: formData.item_list.map(item => ({
                count: item.units,
                weight: item.additional.weight * 1000,
                height: 10, width: 10, length: 10
            })),
            orderReadyDate: formatDate(formData.pickup_date, "yyyy-MM-dd"),
            orderType: "FORWARD",
            paymentType: formData.payment_mode === 'PREPAID' ? 'PREPAID' : 'COD',
            appointmentDelivery: formData.appointment_delivery
        };
        postGeneric(apiConstants.CREATE_PICKUP_REQUEST, request)
            .then(() => {
                enqueueSnackbar("Successfully Created!", { variant: 'success' });
                navigate("/ptl-pickup-requests");
            })
            .catch((err) => {
                const message = err?.response?.data?.message || err.message;
                enqueueSnackbar(message || "Error creating order", { variant: "error" });
            })
            .finally(() => { setIsCreatingOrder(false); });
    };

    return (
        <FormCard>
            {(isLoading || isFetching) && <Loader />}
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2.5}>

                    {/* Merchant */}
                    <Grid item xs={12}>
                        <FieldLabel>Merchant *</FieldLabel>
                        <MerchantFilterCached />
                        {!isMerchantSelected && (
                            <Box sx={{
                                mt: 1, px: 1.5, py: 1, borderRadius: '6px',
                                bgcolor: '#fffbeb', border: '1px solid #fde68a',
                                fontSize: '12px', color: '#92400e', display: 'flex', alignItems: 'center', gap: '6px',
                            }}>
                                ⚠️ Please select a merchant first to proceed.
                            </Box>
                        )}
                    </Grid>

                    {/* Pickup location */}
                    <Grid item xs={12}>
                        <FieldLabel>Pickup Location *</FieldLabel>
                        <RHFSearchableSelectField
                            options={pickupLocationOptions}
                            width="100%"
                            label=""
                            name="warehouseName"
                            autoFocus={true}
                            variant="outlined"
                            disabled={!isMerchantSelected}
                        />
                        <Box sx={{ mt: '4px', fontSize: '11px', color: '#6b7280' }}>
                            Pickup Location not listed?{' '}
                            <Box component="a" href="/merchant-step2" target="_blank"
                                sx={{ color: '#1d4ed8', textDecoration: 'none', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}>
                                Create one here
                            </Box>
                        </Box>
                    </Grid>

                    {/* Delivery Zip */}
                    <Grid item xs={12} md={3}>
                        <FieldLabel>Delivery Zip Code *</FieldLabel>
                        <RHFTextField name="zip" label="" placeholder="6-digit zip" disabled={!isMerchantSelected} />
                    </Grid>

                    {/* Pickup Date */}
                    <Grid item xs={12} md={3}>
                        <FieldLabel>Pickup Date *</FieldLabel>
                        <RHFDateField
                            name="pickup_date"
                            label=""
                            minDate={(() => {
                                const now = new Date();
                                const istTime = new Date(now.getTime() + 5.5 * 60 * 60 * 1000);
                                const currentHour = istTime.getUTCHours();
                                if (currentHour < 13) {
                                    const today = new Date(); today.setHours(0, 0, 0, 0); return today;
                                } else {
                                    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1); tomorrow.setHours(0, 0, 0, 0); return tomorrow;
                                }
                            })()}
                            size="medium"
                            disabled={!isMerchantSelected}
                        />
                    </Grid>

                    {/* Payment Mode */}
                    <Grid item xs={12} md={4}>
                        <FieldLabel>Payment Mode *</FieldLabel>
                        <RHFRadioGroup
                            label=""
                            name="payment_mode"
                            options={[
                                { label: 'PREPAID', value: 'PREPAID' },
                                { label: 'COD', value: 'COD' },
                            ]}
                            disabled={!isMerchantSelected}
                        />
                    </Grid>

                    {/* Items */}
                    <Grid item xs={12}>
                        <Box sx={{ fontSize: '11px', color: '#6b7280', mb: 1 }}>
                            All package weights must be specified in <strong>kg</strong>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {fields.map((field, index) => (
                                <Box key={field.id} sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                    bgcolor: '#eff6ff', borderRadius: '8px',
                                    p: 1.5, border: '1px solid #bfdbfe',
                                }}>
                                    <Box sx={{ flex: 1 }}>
                                        <FieldLabel>No. of Boxes</FieldLabel>
                                        <RHFTextField name={`item_list.${index}.units`} label="" placeholder="e.g. 5" type="number" disabled={!isMerchantSelected} />
                                    </Box>
                                    <Box sx={{ flex: 1 }}>
                                        <FieldLabel>Weight (kg)</FieldLabel>
                                        <RHFTextField name={`item_list.${index}.additional.weight`} label="" placeholder="e.g. 10.5" type="number" disabled={!isMerchantSelected} />
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5, mt: 2.5 }}>
                                        {((index + 1) === fields.length) && (
                                            <IconButton size="small" color="primary" onClick={() => append(emptyItemObject)} disabled={!isMerchantSelected}>
                                                <AddCircleTwoTone />
                                            </IconButton>
                                        )}
                                        {fields.length !== 1 && (
                                            <IconButton size="small" color="error" onClick={() => remove(index)} disabled={!isMerchantSelected}>
                                                <RemoveCircleTwoTone />
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Grid>

                    {/* Actions */}
                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <LoadingButton
                                variant="contained"
                                size="small"
                                type="submit"
                                loading={isCreatingOrder}
                                disabled={!isMerchantSelected}
                                disableElevation
                                sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
                            >
                                Create Shipment
                            </LoadingButton>
                            <Button
                                variant="contained"
                                size="small"
                                disableElevation
                                onClick={() => reset()}
                                sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                            >
                                Reset
                            </Button>
                        </Box>
                    </Grid>

                </Grid>
            </FormProvider>
        </FormCard>
    );
}

// ─── Bulk upload form ─────────────────────────────────────────────────────────

function BulkQuickPickupForm() {
    const { enqueueSnackbar } = useSnackbar();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [lastResult, setLastResult] = useState(null);

    const methods = useForm({ defaultValues: { file: null } });
    const { handleSubmit, reset } = methods;

    const onSubmit = async (values) => {
        if (!values.file || values.file.length === 0) {
            enqueueSnackbar('Please select a file to upload', { variant: 'error' });
            return;
        }
        setIsSubmitting(true);
        setLastResult(null);
        try {
            const formData = new FormData();
            formData.append('file', values.file[0]);
            const res = await post(apiConstants.BULK_QUICK_PICKUP_CREATE, formData);
            const data = res?.data || {};
            setLastResult(data);
            const failed = (data.failedRows || []).length;
            const saved = data.ordersSaved || 0;
            if (data.success) {
                enqueueSnackbar(`Bulk upload successful. Saved ${saved} pickup request(s).`, { variant: 'success' });
            } else {
                enqueueSnackbar(`Uploaded with errors. Saved ${saved}, failed ${failed}.`, { variant: 'warning' });
            }
            reset({ file: null });
        } catch (err) {
            const message = err?.response?.data?.message || err.message;
            enqueueSnackbar(message || 'Bulk upload failed', { variant: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <FormCard>
            <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2.5}>

                    <Grid item xs={12}>
                        <Box sx={{ fontSize: '13px', color: 'text.secondary', mb: 1 }}>
                            <Box component="strong" sx={{ color: 'text.primary' }}>Step 1:</Box> Download the sample Excel file. Each row must include a Merchant ID.
                        </Box>
                        <DownloadSampleFile fileName="QUICK_PICKUP_SAMPLE" excel={true} label="Download Sample File" />
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ fontSize: '13px', color: 'text.secondary', mb: 1 }}>
                            <Box component="strong" sx={{ color: 'text.primary' }}>Step 2:</Box> Upload the filled Excel file (.xlsx).
                        </Box>
                        <RHFFileField name="file" required={true} fileType={['xlsx']} label="Click to upload Excel file" />
                    </Grid>

                    <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <LoadingButton
                                variant="contained"
                                size="small"
                                type="submit"
                                startIcon={<UploadIcon />}
                                loading={isSubmitting}
                                disableElevation
                                sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
                            >
                                Upload
                            </LoadingButton>
                            <Button
                                variant="contained"
                                size="small"
                                disableElevation
                                onClick={() => { reset(); setLastResult(null); }}
                                sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, bgcolor: '#ef4444', '&:hover': { bgcolor: '#dc2626' } }}
                            >
                                Reset
                            </Button>
                        </Box>
                    </Grid>

                    {lastResult && (
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ bgcolor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', p: 2 }}>
                                <Box sx={{ fontSize: '13px', fontWeight: 700, color: '#1a1a2e', mb: 1.5 }}>Last Upload Summary</Box>
                                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: lastResult.failedRows?.length ? 1.5 : 0 }}>
                                    {[
                                        { label: 'File', value: lastResult.fileName },
                                        { label: 'Total Rows', value: lastResult.totalRows },
                                        { label: 'Saved', value: lastResult.ordersSaved, color: '#16a34a' },
                                        { label: 'Failed', value: (lastResult.failedRows || []).length, color: '#dc2626' },
                                    ].map(({ label, value, color }) => (
                                        <Box key={label}>
                                            <Box sx={{ fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px', mb: '2px' }}>{label}</Box>
                                            <Box sx={{ fontSize: '13px', fontWeight: 600, color: color || 'text.primary' }}>{value ?? '—'}</Box>
                                        </Box>
                                    ))}
                                </Box>
                                {(lastResult.failedRows || []).length > 0 && (
                                    <Box sx={{ p: 1.5, bgcolor: '#fef2f2', borderRadius: '6px', border: '1px solid #fecaca', maxHeight: 200, overflow: 'auto' }}>
                                        {(lastResult.failedRows || []).map((r, i) => (
                                            <Box key={i} sx={{ fontSize: '11px', color: '#dc2626', lineHeight: 1.6 }}>
                                                Row {r.rowNumber}: {r.error}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    )}

                </Grid>
            </FormProvider>
        </FormCard>
    );
}

// ─── Page wrapper ─────────────────────────────────────────────────────────────

function CreateQuickPickupRequest() {
    const [tab, setTab] = useState(0);
    return (
        <>
            <PageTitle>Quick Pickup Request</PageTitle>
            <PillFilters pills={PILLS} value={tab} onChange={setTab} />
            {tab === 0 && <SingleQuickPickupForm />}
            {tab === 1 && <BulkQuickPickupForm />}
        </>
    );
}

export default CreateQuickPickupRequest;
